import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import type { ReactNode } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import {
  ThumbLikeRegular,
  DismissSquareRegular,
} from "@fluentui/react-icons";
import type { IApprovalRequest, ApprovalAction } from "@/types/approvalTypes";
import { PAGE_SIZE } from "@/constants/appConstants";
import { createIsLastRowChecker, createLastRowTabHandler, suppressGridTabHandler } from "@/utils/gridKeyboardNavigation";
import styles from "./ApprovalList.module.scss";

ModuleRegistry.registerModules([AllCommunityModule]);

interface IApprovalListProps {
  approvals: IApprovalRequest[];
  actionInProgress: string | null;
  onAction: (approvalId: string, action: ApprovalAction) => Promise<void>;
}

interface IPendingApprovalAction {
  approvalId: string;
  requestor: string;
  partnerName: string;
  action: ApprovalAction;
}

function parseApprovalDate(dateStr: string): number {
  // Expected format: "MM/DD/YYYY HH:mm"
  const [datePart, timePart] = dateStr.split(" ");
  if (!datePart || !timePart) {
    return 0;
  }
  const [month, day, year] = datePart.split("/");
  const [hours, minutes] = timePart.split(":");

  const numMonth = Number(month);
  const numDay = Number(day);
  const numYear = Number(year);
  const numHours = Number(hours);
  const numMinutes = Number(minutes);

  if ([numMonth, numDay, numYear, numHours, numMinutes].some(Number.isNaN)) {
    return 0;
  }

  return new Date(
    numYear,
    numMonth - 1,
    numDay,
    numHours,
    numMinutes,
  ).getTime();
}

function PartnerOneIdCellRenderer(
  params: ICellRendererParams<IApprovalRequest, string>,
): ReactNode {
  return <span className={styles.partnerOneId}>{String(params.value ?? "")}</span>;
}

function SecondaryTextCellRenderer(
  params: ICellRendererParams<IApprovalRequest, string>,
): ReactNode {
  return <span className={styles.secondaryText}>{String(params.value ?? "")}</span>;
}

interface IActionCellRendererParams
  extends ICellRendererParams<IApprovalRequest> {
  context: {
    onActionRequested: (
      approvalId: string,
      requestor: string,
      partnerName: string,
      action: ApprovalAction,
    ) => void;
    actionInProgress: string | null;
    isLastRow: (id: string) => boolean;
  };
}

function ActionCellRenderer(params: IActionCellRendererParams): ReactNode {
  const data = params.data as IApprovalRequest | undefined;

  if (!data) {
    return null;
  }

  const { id, requestor, partnerName, status } = data;

  if (status === "Approved") {
    return (
      <div className={styles.actionButtons}>
        <span className={styles.statusLabel} aria-label={`Approved request by ${requestor} for ${partnerName}`}>
          <ThumbLikeRegular className={styles.statusIcon} aria-hidden="true" />
          <span>Approved</span>
        </span>
      </div>
    );
  }

  if (status === "Rejected") {
    return (
      <div className={styles.actionButtons}>
        <span className={styles.statusLabel} aria-label={`Rejected request by ${requestor} for ${partnerName}`}>
          <DismissSquareRegular className={styles.statusIcon} aria-hidden="true" />
          <span>Rejected</span>
        </span>
      </div>
    );
  }

  const { onActionRequested, actionInProgress, isLastRow } = params.context;
  const isProcessing = actionInProgress === id;
  const isLast = isLastRow(id);

  if (isProcessing) {
    return (
      <div className={styles.actionButtons}>
        <span className={styles.processingLabel} aria-label={`Processing request by ${requestor} for ${partnerName}`}>
          <Spinner size="tiny" labelPosition="after" label="Processing..." />
        </span>
      </div>
    );
  }

  const handleActionClick = (action: ApprovalAction): void => {
    onActionRequested(id, requestor, partnerName, action);
  };

  const handleRejectButtonKeyDown = createLastRowTabHandler(isLast, styles.wrapper);

  return (
    <div className={styles.actionButtons}>
      <Button
        appearance="transparent"
        className={styles.approveButton}
        aria-label={`Approve request by ${requestor} for ${partnerName}`}
        disabled={isProcessing}
        onClick={(): void => {
          handleActionClick("Approve");
        }}
        icon={<ThumbLikeRegular className={styles.approveIcon} aria-hidden="true" />}
      >
        <span>Approve</span>
      </Button>
      <Button
        appearance="transparent"
        className={styles.rejectButton}
        aria-label={`Reject request by ${requestor} for ${partnerName}`}
        disabled={isProcessing}
        onClick={(): void => {
          handleActionClick("Reject");
        }}
        onKeyDown={handleRejectButtonKeyDown}
        icon={<DismissSquareRegular className={styles.rejectIcon} aria-hidden="true" />}
      >
        <span>Reject</span>
      </Button>
    </div>
  );
}

const COLUMN_DEFS: ColDef<IApprovalRequest>[] = [
  {
    headerName: "PartnerOne ID",
    field: "partnerOneId",
    flex: 15,
    cellRenderer: PartnerOneIdCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Partner name",
    field: "partnerName",
    flex: 25,
    cellRenderer: SecondaryTextCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Requestor",
    field: "requestor",
    flex: 30,
    cellRenderer: SecondaryTextCellRenderer,
    sortable: false,
    suppressNavigable: true,
  },
  {
    headerName: "Action",
    field: "status",
    flex: 30,
    cellRenderer: ActionCellRenderer,
    sortable: false,
    suppressKeyboardEvent: suppressGridTabHandler,
  },
];

export function ApprovalList({
  approvals,
  actionInProgress,
  onAction,
}: IApprovalListProps): ReactNode {
  const [pendingAction, setPendingAction] = useState<IPendingApprovalAction | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<IApprovalRequest>>(null);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [approvals, actionInProgress]);

  const handleActionRequested = useCallback(
    (approvalId: string, requestor: string, partnerName: string, action: ApprovalAction): void => {
      setPendingAction({ approvalId, requestor, partnerName, action });
      setIsDialogOpen(true);
    },
    [],
  );

  const handleDialogOpenChange = useCallback((isOpen: boolean): void => {
    if (!isOpen) {
      setIsDialogOpen(false);
      setPendingAction(null);
    }
  }, []);

  const handleConfirmAction = useCallback(async (): Promise<void> => {
    if (!pendingAction) {
      return;
    }

    await onAction(pendingAction.approvalId, pendingAction.action);
    setIsDialogOpen(false);
  }, [onAction, pendingAction]);

  const isConfirmDisabled =
    pendingAction !== null && actionInProgress === pendingAction.approvalId;

  const sortedApprovals = useMemo(
    () =>
      [...approvals].sort(
        (a, b) =>
          parseApprovalDate(b.requestedOn) - parseApprovalDate(a.requestedOn),
      ),
    [approvals],
  );

  const defaultColDef = useMemo<ColDef<IApprovalRequest>>(
    () => ({
      resizable: false,
      filter: false,
      suppressMovable: true,
      wrapText: true,
      autoHeight: true,
    }),
    [],
  );

  const getRowId = useCallback(
    (params: { data: IApprovalRequest }): string => params.data.id,
    [],
  );

  const isLastRow = useMemo(
    () => createIsLastRowChecker(gridRef, sortedApprovals, PAGE_SIZE),
    [sortedApprovals],
  );

  const gridContext = useMemo(
    () => ({ onActionRequested: handleActionRequested, actionInProgress, isLastRow }),
    [handleActionRequested, actionInProgress, isLastRow],
  );

  if (sortedApprovals.length === 0) {
    return (
      <div className={styles.emptyState} role="status">
        No approvals found.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(_event, data): void => {
          handleDialogOpenChange(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm {pendingAction?.action ?? "action"}</DialogTitle>
            <DialogContent>
              {`Are you sure you want to ${pendingAction?.action.toLowerCase() ?? "perform this action on"} the request raised by ${pendingAction?.requestor ?? ""} for the partner ${pendingAction?.partnerName ?? ""}?`}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={(): void => {
                  setIsDialogOpen(false);
                }}
                disabled={isConfirmDisabled}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={(): void => {
                  void handleConfirmAction();
                }}
                disabled={isConfirmDisabled}
              >
                {isConfirmDisabled ? (
                  <span className={styles.confirmButtonLoading}>
                    <Spinner size="tiny" />
                    <span>Processing...</span>
                  </span>
                ) : (
                  "Confirm"
                )}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <div className={styles.gridWrapper}>
        <div className={styles.grid}>
          <AgGridReact<IApprovalRequest>
            ref={gridRef}
            rowData={sortedApprovals}
            columnDefs={COLUMN_DEFS}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            context={gridContext}
            domLayout="autoHeight"
            pagination
            paginationPageSize={PAGE_SIZE}
            suppressRowHoverHighlight
            enableCellTextSelection
            ensureDomOrder
            headerHeight={46}
          />
        </div>
      </div>
    </div>
  );
}
